interface Route {
    to: string;
    slug?: string;
    viewId?: string | null;
}

export type RouteHandler = (route: Route) => void | Promise<void>;

interface Routes {
    [key: string]: RouteHandler;
}

const ROUTE_TO_PARAM = 'route';
const ROUTE_SLUG_PARAM = 'slug';
const ROUTE_LINKED_VIEW_PARAM = 'viewId';

/**
 * @description Creates a hook, with which you can handle client side routing
 * @param {{ routes: Routes; defaultRoute: Route }} { routes, defaultRoute }
 * @return {*}
 */
const createRoutingHook = ({ routes, defaultRoute }: { routes: Routes; defaultRoute: Route }) => {
    let currentRoute: Route | undefined;

    init();

    function init() {
        window.addEventListener('popstate', (e) => {
            const urlParamRoute = parseUrlParams();

            if (urlParamRoute) {
                setRoute(urlParamRoute, false);
            }

            e.preventDefault();
        });
    }

    /**
     * @description Set's the current route to passed route. When pushState is false, the url & history won't be updated.
     * @param {Route} route
     * @param {boolean} [pushState=true]
     */
    function setRoute(route: Route, pushState: boolean = true) {
        currentRoute = route;

        const url = appendUrlParams(route);

        routes[route.to](route);

        if (pushState) history.pushState(currentRoute, '', url);
    }

    /**
     * @description Will call the current route again, optionally without updating the url & history
     */
    function updateCurrentRoute(options?: Partial<Route> & { updateHistory?: boolean }) {
        if (currentRoute) {
            const { updateHistory, ...route } = options || {};
            setRoute({ ...currentRoute, ...route }, updateHistory ?? true);
        }
    }

    function parseUrlParams(): Route | void {
        const url = new URL(document.location.href);

        const to = url.searchParams.get(ROUTE_TO_PARAM);
        const slug = url.searchParams.get(ROUTE_SLUG_PARAM);
        const viewId = url.searchParams.get(ROUTE_LINKED_VIEW_PARAM);

        if (to) {
            return {
                to: to as Route['to'],
                slug: slug ?? undefined,
                viewId: viewId ?? undefined,
            };
        }
    }

    function appendUrlParams(route: Route): string {
        const url = new URL(document.location.href);

        url.searchParams.set(ROUTE_TO_PARAM, route.to);

        if (route.slug) {
            url.searchParams.set(ROUTE_SLUG_PARAM, route.slug);
        } else {
            url.searchParams.delete(ROUTE_SLUG_PARAM);
        }

        if (route.viewId) {
            url.searchParams.set(ROUTE_LINKED_VIEW_PARAM, route.viewId);
        } else {
            url.searchParams.delete(ROUTE_LINKED_VIEW_PARAM);
        }

        return url.toString();
    }

    /**
     * @description Call setInitialRoute() when the component is ready to initialise the first route
     */
    function setInitialRoute() {
        const urlParamRoute = parseUrlParams();

        setRoute(urlParamRoute || defaultRoute);
    }

    return () => {
        return {
            setInitialRoute,
            setRoute,
            updateCurrentRoute,
        };
    };
};

export { createRoutingHook, Route, Routes };
